// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Column,
  MdPadding,
  Row,
  Toast,
  ToastContext,
} from '@alto/design-system';
import { useGetDeliveryMethodsByShipmentIDs } from '@alto/features';
import React, { useContext, useEffect, useState } from 'react';
import { rateShipmentDismissed } from '~shared/actions/rateShipment';
import { getIsTippableForShipmentID } from '~shared/features/checkout/selectors/getIsTippable';
import { canTipAfterRating } from '~shared/features/courier-tips/helpers';
import { getShipmentTipForShipmentID } from '~shared/features/courier-tips/selectors';
import { NO_RATING_ERROR_MESSAGE, createSurveyResponseDataPayload } from '~shared/features/rate-shipment/helpers';
import { getShipmentWithID } from '~shared/features/shipments/selectors/getShipments';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useGetPendingRateShipment } from '~shared/queries/useRateShipment';
import { type SurveyResponsePayload, useSubmitSurveyResponse } from '~shared/queries/useSubmitSurveyResponse';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { TipShipmentActionSheet } from './TipShipmentActionSheet';
import { RateShipment } from '~web/features/rate-shipment/RateShipment';

export const RateShipmentActionSheet = () => {
  const dispatch = useDispatchShared();
  const { mutateAsync: submitSurveyResponse } = useSubmitSurveyResponse();
  const { trackEvent } = useAnalytics();
  const { closeActionSheet, overrideActionSheetStack } = useContext(ActionSheetContext);
  const { addToast } = useContext(ToastContext);

  const { pendingRating } = useGetPendingRateShipment();
  const uuid = pendingRating?.uuid;
  const shipmentID = pendingRating?.shipment_id;
  const shipment = useSelectorShared((state) => getShipmentWithID(state, { shipmentID }));
  const shipmentTip = useSelectorShared((state) => getShipmentTipForShipmentID(state, { shipmentID }));
  const { deliveryMethodsByShipmentID } = useGetDeliveryMethodsByShipmentIDs(shipmentID ? [shipmentID] : []);
  const deliveryMethod = shipmentID ? deliveryMethodsByShipmentID[shipmentID] : undefined;
  const { insurances } = useGetInsurances();
  const isTippable = useSelectorShared((state) =>
    getIsTippableForShipmentID(state, { shipmentID, deliveryMethod, insurances }),
  );

  const [rating, setRating] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [freeformReason, setFreeformReason] = useState('');

  useEffect(() => {
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__EXPERIENCE_VIEWED,
      params: {
        view: 'modal',
        uuid,
      },
    });
  }, [trackEvent, uuid]);

  const onRatingChangeWithAnalytics = (rating: number) => {
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__RATING_CLICKED,
      params: {
        view: 'modal',
        rating,
        uuid,
      },
    });
    setErrorMessage('');
    setRating(rating);
  };

  const handleDismissRating = () => {
    closeActionSheet();
    dispatch(rateShipmentDismissed(uuid));
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__DISMISS_CLICKED,
      params: {
        view: 'modal',
        uuid,
      },
    });
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      setErrorMessage(NO_RATING_ERROR_MESSAGE);
    } else {
      trackEvent({
        event: EVENTS.RATE_SHIPMENT__SUBMIT_CLICKED,
        params: { uuid, view: 'modal' },
      });

      const surveyResponse: SurveyResponsePayload = {
        name: 'rate_shipment',
        data: createSurveyResponseDataPayload(rating, freeformReason),
        survey_uuid: uuid ?? undefined,
        version: 2,
      };
      const submitted = await submitSurveyResponse(surveyResponse)
        .then(() => true)
        .catch(() => false);

      if (submitted) {
        const isEligibleToTip = canTipAfterRating({
          isTippable,
          rating,
          shipment,
          shipmentTip,
        });

        if (isEligibleToTip && shipmentID) {
          // override the action sheet tack because the shipment cannot be rated again
          overrideActionSheetStack([
            <TipShipmentActionSheet
              key="tip-shipment-action-sheet"
              shipmentID={shipmentID}
            />,
          ]);
        } else {
          addToast(<Toast>Thank you for your rating!</Toast>);
          closeActionSheet();
        }
      }
    }
  };

  return (
    <ActionSheetV2 analyticsName="rate_shipment">
      <Column gap="MD">
        <RateShipment
          rating={rating}
          onRatingChange={onRatingChangeWithAnalytics}
          freeformReason={freeformReason}
          onFreeformReasonChange={setFreeformReason}
          errorMessage={errorMessage}
          isModal
        />
        <MdPadding
          topPadding={SPACING.STATIC.NONE}
          bottomPadding={SPACING.STATIC.NONE}
          leftPadding={SPACING.STATIC.NONE}
        >
          <Row
            right
            gap="SM"
          >
            <Button
              label="Dismiss"
              accessibilityLabel="Press to close modal"
              onPress={handleDismissRating}
              type="secondary"
            />
            <Button
              label="Submit"
              accessibilityLabel="Press to submit"
              onPress={handleSubmitRating}
            />
          </Row>
        </MdPadding>
      </Column>
    </ActionSheetV2>
  );
};
